import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { User, LoginResponse } from '../types';
import { checkForUpdates } from '../serviceWorkerRegistration';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('token');
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('token');
  });
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      setIsAuthenticated(true);
      // Rehidratar usuario desde localStorage si existe (tras refresh)
      const storedUsername =
        localStorage.getItem('username') ||
        localStorage.getItem('userName') ||
        localStorage.getItem('user') ||
        '';
      const storedRolesRaw = localStorage.getItem('roles');
      let storedRoles: string[] = [];
      try {
        storedRoles = storedRolesRaw ? JSON.parse(storedRolesRaw) : [];
      } catch {
        storedRoles = [];
      }
      if (storedUsername) {
        setUser({ username: storedUsername, roles: storedRoles } as User);
      }
      // Verificar actualizaciones si ya está autenticado (refresh de página)
      checkForUpdates();
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post<LoginResponse>('/api/auth/login', { username, password });
      const { token: newToken, refreshToken, username: userUsername, roles } = response.data;

      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', refreshToken);
      // Persistir identidad básica para rehidratación post-refresh
      localStorage.setItem('username', userUsername);
      localStorage.setItem('userName', userUsername);
      localStorage.setItem('user', userUsername);
      localStorage.setItem('roles', JSON.stringify(roles || []));

      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      setUser({ username: userUsername, roles } as User);
      setToken(newToken);
      setIsAuthenticated(true);

      // Verificar actualizaciones del service worker después del login
      checkForUpdates();
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    // Limpiar selección de grupo de caja al cerrar sesión
    localStorage.removeItem('grupoCajaChicaSeleccionado');
    localStorage.removeItem('grupoCajaMcp');
    // Limpiar identidad persistida
    localStorage.removeItem('username');
    localStorage.removeItem('userName');
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        logout();
        return;
      }

      const response = await api.post<LoginResponse>('/api/auth/refresh-token', refreshToken);
      const { token: newToken, refreshToken: newRefreshToken } = response.data;

      if (!newToken || !newRefreshToken) {
        throw new Error('Token o refreshToken no recibidos');
      }

      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);

      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
    } catch (error) {
      console.error('Error al actualizar el token:', error);
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      token,
      login,
      logout,
      refreshToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}; 